import { useTranslation } from 'react-i18next';
import { useUpdateGlossary } from '@utils/api/glossaries/use-update-glossary.tsx';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister
} from 'react-hook-form';
import Input from '@components/atoms/ui-kit/input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';
import { Dispatch, SetStateAction } from 'react';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';
import { UpdateGlossaryRequest } from '@utils/types/glossary';
import { useThemeContext } from '@utils/providers/theme-context';

type Props = {
  glossaryId: number;
  control: Control<UpdateGlossaryRequest>;
  errors: FieldErrors<UpdateGlossaryRequest>;
  isModalOpen: boolean;
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmit: UseFormHandleSubmit<UpdateGlossaryRequest, any>;
  register: UseFormRegister<UpdateGlossaryRequest>;
};

export default function EditGlossaryModal({
  glossaryId,
  control,
  errors,
  isModalOpen,
  setIsModalOpen,
  handleSubmit,
  register
}: Props) {
  const { t } = useTranslation();
  const { mutate: updateGlossary, isLoading } = useUpdateGlossary(glossaryId);
  const { getColour } = useThemeContext();

  const onSubmitUpdate = (data: UpdateGlossaryRequest) => {
    updateGlossary(data, {
      onSuccess: () => {
        toast.success(t('glossary-updated-successfully'));
        setIsModalOpen(false);
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error?.response?.data.detail ||
          error?.response?.data.error ||
          error?.response?.data.message ||
          t('an-error-occurred-while-updating-the-glossary');

        toast.error(errorMessage);
      }
    });
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      closeOnOutsideClick={false}
      size="medium"
      title={t('edit-glossary')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => setIsModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="smoke"
        />,
        <Button
          key="update"
          type="submit"
          onClick={handleSubmit(onSubmitUpdate)}
          label={t('update')}
          size="small"
          variant="primary"
          disabled={isLoading}
        />
      ]}
    >
      <form onSubmit={handleSubmit(onSubmitUpdate)}>
        <div className="grid grid-cols-1 gap-4">
          <Input
            id="name"
            label={t('glossary-name')}
            type="text"
            placeholder={t('glossary-name-placeholder')}
            {...register('name')}
            error={errors.name && errors.name.message}
          />

          <Input
            id="description"
            label={t('description')}
            type="text"
            multiline
            placeholder={t('glossary-description-placeholder')}
            {...register('description')}
            error={errors.description && errors.description.message}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="is_active"
                name={field.name}
                ref={field.ref}
                checked={!!field.value}
                onChange={e => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                label={t('active')}
                variant="primary"
                wrapperClass="flex h-6 items-center"
                error={errors.is_active && errors.is_active.message}
              />
            )}
          />

          <div
            className="p-3 rounded-2xl text-sm"
            style={{
              backgroundColor: getColour('appSoft'),
              color: getColour('inactiveText'),
              border: `1px solid ${getColour('hairline')}`
            }}
          >
            {t('glossary-entries-cannot-be-edited')}
          </div>
        </div>
      </form>
    </Modal>
  );
}
